import griImg from "../../../Assets/gri.png";
import csrdImg from "../../../Assets/csrd.png";
import vsmeImg from "../../../Assets/vsme.png";
import ammcImg from "../../../Assets/ammc.jpg";

export type CriterionLevel = "Critique" | "Majeur" | "Standard";
export type CriterionStatus = "A verifier" | "Conforme" | "Partiel" | "Non conforme" | "Non applicable";

export interface GriCriterion {
  id: string;
  code: string;
  name: string;
  theme: string;
  level: CriterionLevel;
  description: string;
  responseHint: string;
  documentTypes: string[];
  requiredDocs: number;
}

export interface CriterionState {
  status: CriterionStatus;
  response: string;
  selectedDocType: string;
  uploadedFiles: Array<{ name: string; size: number }>;
}

export interface NormConfig {
  key: string;
  label: string;
  fullName: string;
  img: string;
  color: string;
  implemented: boolean;
}

export const NORMS: NormConfig[] = [
  { key: "GRI",  label: "GRI",  fullName: "Global Reporting Initiative",                 img: griImg,  color: "#1a6b3c", implemented: true  },
  { key: "CSRD", label: "CSRD", fullName: "Corporate Sustainability Reporting Directive", img: csrdImg, color: "#1e40af", implemented: false },
  { key: "VSME", label: "VSME", fullName: "Voluntary SME Sustainability Standard",        img: vsmeImg, color: "#7c3aed", implemented: false },
  { key: "AMMC", label: "AMMC", fullName: "Autorité Marocaine du Marché des Capitaux",   img: ammcImg, color: "#b45309", implemented: false },
];

export const GRI_THEMES = [
  "Standards Universels",
  "Environnement",
  "Social et Droits Humains",
  "Gouvernance & Éthique",
] as const;

export const GRI_CRITERIA: GriCriterion[] = [
  {
    id: "GRI-2", code: "GRI 2", name: "General Disclosures", theme: "Standards Universels", level: "Critique",
    description: "Informations générales sur l'organisation, sa gouvernance, sa stratégie, ses pratiques et ses processus de reporting.",
    responseHint: "objectifs RSE, gouvernance, structure organisationnelle, implantations géographiques",
    documentTypes: ["Rapport annuel", "Rapport RSE", "Statuts d'entreprise", "Organigramme", "Politique RSE générale"],
    requiredDocs: 5,
  },
  {
    id: "GRI-3", code: "GRI 3", name: "Material Topics", theme: "Standards Universels", level: "Critique",
    description: "Processus de détermination des sujets importants et liste des sujets matériels retenus pour le reporting.",
    responseHint: "processus de matérialité, parties prenantes consultées, sujets matériels retenus et justification",
    documentTypes: ["Rapport de matérialité", "Résultats consultation parties prenantes", "Analyse des impacts"],
    requiredDocs: 4,
  },
  {
    id: "GRI-301", code: "GRI 301", name: "Materials", theme: "Environnement", level: "Standard",
    description: "Consommation de matières premières, matériaux renouvelables et recyclés dans la production.",
    responseHint: "flux de matières premières, part de matériaux recyclés, objectifs de réduction",
    documentTypes: ["Rapport consommations matières", "Politique achats durables", "Inventaire matériaux"],
    requiredDocs: 3,
  },
  {
    id: "GRI-302", code: "GRI 302", name: "Energy", theme: "Environnement", level: "Critique",
    description: "Consommation énergétique, efficacité énergétique, énergies renouvelables et objectifs de réduction.",
    responseHint: "consommations par type d'énergie (kWh, GJ), part renouvelable, objectifs d'efficacité et initiatives",
    documentTypes: ["Factures énergétiques", "Politique énergie/climat", "Plan efficacité énergétique", "Rapport audit énergétique", "Certification ISO 50001"],
    requiredDocs: 5,
  },
  {
    id: "GRI-303", code: "GRI 303", name: "Water and Effluents", theme: "Environnement", level: "Majeur",
    description: "Interaction avec l'eau, prélèvements, consommation, rejets et impacts sur les ressources en eau.",
    responseHint: "volumes prélevés et rejetés par source, zones de stress hydrique, actions de réduction",
    documentTypes: ["Données consommation eau", "Politique eau", "Rapport effluents", "Permis environnementaux"],
    requiredDocs: 4,
  },
  {
    id: "GRI-304", code: "GRI 304", name: "Biodiversity", theme: "Environnement", level: "Majeur",
    description: "Impacts sur la biodiversité, zones protégées concernées et mesures de protection/compensation.",
    responseHint: "sites en zones sensibles, impacts identifiés, mesures d'évitement et de compensation",
    documentTypes: ["Étude d'impact environnemental", "Politique biodiversité", "Rapport sites sensibles"],
    requiredDocs: 3,
  },
  {
    id: "GRI-305", code: "GRI 305", name: "Emissions", theme: "Environnement", level: "Critique",
    description: "Émissions de GES scope 1, 2 et 3, intensité des émissions et objectifs climatiques.",
    responseHint: "bilan GES scope 1 & 2 (tCO2eq), scope 3 identifié, objectifs de réduction et trajectoire",
    documentTypes: ["Bilan carbone", "Politique climat", "Données émissions GES", "Plan de réduction", "Certification ISO 14064"],
    requiredDocs: 5,
  },
  {
    id: "GRI-306", code: "GRI 306", name: "Waste", theme: "Environnement", level: "Majeur",
    description: "Production et gestion des déchets par type (dangereux/non dangereux) et destination (valorisation, élimination).",
    responseHint: "tonnage par catégorie et filière, taux de valorisation, déchets dangereux, objectifs de réduction",
    documentTypes: ["Registre déchets", "Contrats valorisation déchets", "Politique déchets", "Rapport audit environnemental"],
    requiredDocs: 4,
  },
  {
    id: "GRI-308", code: "GRI 308", name: "Supplier Environmental Assessment", theme: "Environnement", level: "Standard",
    description: "Évaluation et sélection des fournisseurs sur la base de critères environnementaux.",
    responseHint: "% fournisseurs évalués, critères utilisés, actions correctives, fournisseurs à risque identifiés",
    documentTypes: ["Grille évaluation fournisseurs", "Politique achats durables", "Rapports audit fournisseurs"],
    requiredDocs: 3,
  },
  {
    id: "GRI-401", code: "GRI 401", name: "Employment", theme: "Social et Droits Humains", level: "Critique",
    description: "Effectifs, nouvelles embauches, départs, avantages sociaux et sécurité de l'emploi.",
    responseHint: "effectifs par catégorie/genre/âge, taux de rotation, avantages sociaux offerts aux CDI",
    documentTypes: ["Rapport RH annuel", "Bilan social", "Politique emploi", "Données effectifs", "Grille salariale"],
    requiredDocs: 5,
  },
  {
    id: "GRI-403", code: "GRI 403", name: "Occupational Health and Safety", theme: "Social et Droits Humains", level: "Critique",
    description: "Système de management SST, accidents du travail, maladies professionnelles et promotion de la santé.",
    responseHint: "taux de fréquence/gravité accidents, système SMSST, certifications, programmes prévention",
    documentTypes: ["Rapport SST", "Registre accidents du travail", "Document unique évaluation risques", "Certification ISO 45001", "Plan de prévention"],
    requiredDocs: 5,
  },
  {
    id: "GRI-404", code: "GRI 404", name: "Training and Education", theme: "Social et Droits Humains", level: "Majeur",
    description: "Programmes de formation, développement des compétences et entretiens d'évaluation de performance.",
    responseHint: "heures de formation par salarié, dépenses formation/CA, types de programmes, évaluations annuelles",
    documentTypes: ["Plan de formation", "Rapport formations réalisées", "Politique développement compétences", "Entretiens annuels"],
    requiredDocs: 4,
  },
  {
    id: "GRI-405", code: "GRI 405", name: "Diversity and Equal Opportunity", theme: "Social et Droits Humains", level: "Majeur",
    description: "Diversité des organes de gouvernance et des effectifs, égalité salariale et de traitement.",
    responseHint: "répartition genre/âge/nationalité dans governance et effectifs, index égalité H/F, actions d'inclusion",
    documentTypes: ["Index égalité H/F", "Rapport diversité", "Politique diversité-inclusion", "Données démographiques RH"],
    requiredDocs: 4,
  },
  {
    id: "GRI-406", code: "GRI 406", name: "Non-discrimination", theme: "Social et Droits Humains", level: "Critique",
    description: "Incidents de discrimination signalés, mesures correctives prises et politiques de non-discrimination.",
    responseHint: "nombre d'incidents déclarés, mécanismes d'alerte, mesures correctives, formations sensibilisation",
    documentTypes: ["Politique non-discrimination", "Code de conduite", "Procédure signalement", "Rapport incidents RH"],
    requiredDocs: 4,
  },
  {
    id: "GRI-408", code: "GRI 408", name: "Child Labor", theme: "Social et Droits Humains", level: "Critique",
    description: "Risques liés au travail des enfants dans les opérations et la chaîne d'approvisionnement.",
    responseHint: "évaluation des risques par pays/fournisseur, contrôles mis en place, incidents identifiés et traités",
    documentTypes: ["Politique droits humains", "Code de conduite fournisseurs", "Rapport audit chaîne valeur", "Déclaration vigilance"],
    requiredDocs: 4,
  },
  {
    id: "GRI-409", code: "GRI 409", name: "Forced Labor", theme: "Social et Droits Humains", level: "Critique",
    description: "Risques liés au travail forcé dans les opérations propres et la chaîne d'approvisionnement.",
    responseHint: "cartographie des risques travail forcé, due diligence fournisseurs, mécanismes de recours",
    documentTypes: ["Politique droits humains", "Code de conduite fournisseurs", "Rapport audit social", "Déclaration vigilance"],
    requiredDocs: 4,
  },
  {
    id: "GRI-413", code: "GRI 413", name: "Local Communities", theme: "Social et Droits Humains", level: "Majeur",
    description: "Engagement avec les communautés locales, évaluation des impacts et programmes de développement local.",
    responseHint: "consultations communautaires, programmes d'investissement social, impacts positifs/négatifs identifiés",
    documentTypes: ["Rapport impact communautaire", "Programmes RSE locaux", "Concertations parties prenantes"],
    requiredDocs: 3,
  },
  {
    id: "GRI-414", code: "GRI 414", name: "Supplier Social Assessment", theme: "Social et Droits Humains", level: "Majeur",
    description: "Évaluation sociale des fournisseurs, impacts négatifs identifiés et mesures correctives.",
    responseHint: "% fournisseurs évalués socialement, risques identifiés, plan d'actions correctives, ruptures de contrat",
    documentTypes: ["Grille évaluation fournisseurs", "Politique achats responsables", "Audits sociaux fournisseurs"],
    requiredDocs: 3,
  },
  {
    id: "GRI-201", code: "GRI 201", name: "Economic Performance", theme: "Gouvernance & Éthique", level: "Standard",
    description: "Valeur économique générée et distribuée, plans de retraite et implications financières du changement climatique.",
    responseHint: "valeur créée et distribuée (employés, état, actionnaires, communautés), risques climatiques financiers",
    documentTypes: ["Rapport financier annuel", "Comptes annuels", "Rapport impact économique"],
    requiredDocs: 3,
  },
  {
    id: "GRI-205", code: "GRI 205", name: "Anti-corruption", theme: "Gouvernance & Éthique", level: "Critique",
    description: "Évaluation des risques de corruption, politiques anticorruption, formation et incidents confirmés.",
    responseHint: "politique anticorruption, % salariés formés, mécanismes de contrôle, incidents déclarés et traités",
    documentTypes: ["Code d'éthique", "Politique anticorruption", "Formation compliance", "Procédures contrôle interne", "Rapport incidents éthiques"],
    requiredDocs: 5,
  },
  {
    id: "GRI-206", code: "GRI 206", name: "Anti-competitive Behavior", theme: "Gouvernance & Éthique", level: "Majeur",
    description: "Actions en justice liées à des comportements anticoncurrentiels, ententes et violations du droit de la concurrence.",
    responseHint: "politique conformité concurrentielle, formations, procédures engagées et résultats",
    documentTypes: ["Politique conformité concurrentielle", "Code de conduite", "Rapport incidents légaux"],
    requiredDocs: 3,
  },
];

export function initCriteriaStates(): Record<string, CriterionState> {
  const s: Record<string, CriterionState> = {};
  GRI_CRITERIA.forEach((c) => {
    s[c.id] = {
      status: "A verifier",
      response: "",
      selectedDocType: c.documentTypes[0] ?? "",
      uploadedFiles: [],
    };
  });
  return s;
}

const PREFILL_RESPONSES: Record<string, string> = {
  "GRI-2":   "Agro-Industries Maroc S.A. est une société anonyme fondée en 1998, opérant dans la transformation agroalimentaire au Maroc. Le groupe emploie 850 personnes réparties sur 3 sites de production (Agadir, Marrakech, Meknès) et 2 filiales de distribution. La gouvernance RSE est assurée par un Comité Développement Durable présidé par le DG et réunissant les directeurs fonctionnels trimestriellement. Notre stratégie RSE 2023-2027 s'articule autour de 4 piliers : eau, carbone, emploi durable et approvisionnement responsable.",
  "GRI-3":   "La démarche de matérialité a été conduite en 2023 selon la méthodologie de double matérialité. 42 parties prenantes ont été consultées (salariés, clients, fournisseurs, riverains, autorités). Les 5 sujets matériels retenus sont : (1) Eau et effluents, (2) Émissions GES, (3) Santé et sécurité au travail, (4) Conditions de travail et droits sociaux, (5) Approvisionnement responsable. Ces sujets ont été validés par le Conseil d'Administration en mars 2023.",
  "GRI-302": "La consommation énergétique totale du groupe s'est élevée à 24 500 MWh en 2024 (électricité : 18 200 MWh, gaz naturel : 6 300 MWh). La part d'énergies renouvelables atteint 28% grâce à l'installation de 2 400 panneaux photovoltaïques sur le site d'Agadir. L'intensité énergétique est de 145 kWh/tonne produite (vs 162 en 2022). Objectif 2027 : -20% d'intensité énergétique et 50% d'électricité renouvelable.",
  "GRI-305": "Les émissions de GES scope 1 & 2 s'élèvent à 4 820 tCO2eq en 2024 (scope 1 : 1 240 tCO2eq liées au gaz naturel et carburants ; scope 2 market-based : 3 580 tCO2eq). Le scope 3 est partiellement calculé : achats de matières premières (8 900 tCO2eq estimées) et transport amont (1 200 tCO2eq). Objectif SBTi déposé en 2024 : -42% scope 1&2 d'ici 2030 (base 2022).",
  "GRI-401": "Effectifs totaux au 31/12/2024 : 850 salariés (62% femmes, 38% hommes). CDI : 720 (85%), CDD : 80 (9%), intérimaires : 50 (6%). Taux de rotation : 8,2% (CDI). Nouvelles embauches 2024 : 95. La politique d'emploi privilégie le recrutement local (78% des effectifs issus des régions d'implantation). Avantages : mutuelle santé famille, transport, prime d'ancienneté, intéressement.",
  "GRI-403": "Le système de management SST est certifié ISO 45001 depuis 2021. Taux de fréquence des accidents 2024 : 4,2 (vs 6,8 en 2022). Taux de gravité : 0,18. Aucun accident mortel. Les risques prioritaires identifiés sont : manutention manuelle, chutes, produits chimiques (nettoyage). Programme 'Zéro Accident' déployé en 2023 avec 1 200 heures de formation SST dispensées.",
  "GRI-205": "La politique anticorruption du groupe s'applique à l'ensemble des salariés et partenaires. 98% des cadres ont suivi la formation compliance (e-learning 2h) en 2024. Le dispositif d'alerte éthique permet un signalement anonyme (géré par un tiers indépendant). Aucun incident de corruption confirmé en 2024. Les procédures de contrôle interne font l'objet d'un audit annuel par le Comité d'Audit.",
};

export function prefillEvaluationResponse(criterionId: string): string {
  return PREFILL_RESPONSES[criterionId] ?? "";
}

export const CONTEXT_PREFILL = {
  companyName: "Agro-Industries Maroc S.A.",
  sector: "Agriculture & Agroalimentaire",
  size: "250 - 999 salariés",
  countries: ["Maroc", "France"],
  scope: "Toutes les activités du groupe sur le territoire marocain, incluant les sites de production, les unités de conditionnement et les filiales directes. Exclusion des franchisés et distributeurs tiers.",
  mainActivities: ["Production agricole", "Transformation agroalimentaire", "Conditionnement et logistique"],
  environmentalImpacts: ["Consommation d'eau", "Déchets organiques", "Émissions GES scope 1 & 2"],
  socialProfile: ["Saisonniers agricoles", "Travailleurs migrants", "Femmes (65% des effectifs)"],
  criticalSuppliers: ["Semences certifiées", "Emballages biodégradables", "Transport frigorifique"],
  rseFunctions: ["Directeur RSE", "Responsable Environnement HSE", "DRH"],
  rsePolicies: ["Politique environnementale", "Charte sociale", "Code d'éthique fournisseurs"],
  certifications: ["ISO 14001", "GlobalGAP", "SA8000"],
  esgIndicators: ["GES scope 1&2", "Consommation eau/tonne produite", "Taux fréquence accidents"],
  ethicsTopics: ["Protection des données RGPD", "Lutte contre la corruption", "Conflits d'intérêts"],
  docMaturity: ["Documents sources", "Données à consolider", "Preuves à relier"],
  dataAvailability: ["Données extraites des SI", "Données à confirmer"],
  riskSites: "Site de production Souss-Massa (zone de stress hydrique élevé), activités saisonnières avec recours à la main-d'œuvre temporaire, fournisseurs d'emballage plastique en Asie.",
  esgPriorities: "Priorité 1 : Réduction de la consommation d'eau (-20% d'ici 2027). Priorité 2 : Certification carbone de la filière avocats. Contrainte principale : données GES scope 3 non disponibles pour les fournisseurs tier-2.",
  gri_reportingObjective: "Rapport RSE annuel",
  gri_reportingPeriod: "Exercice fiscal 2024 (1er janvier – 31 décembre 2024)",
  gri_externalAssurance: "Assurance limitée",
  gri_reportingBoundaries: "Entités incluses : siège social, 3 sites de production (Agadir, Marrakech, Meknès), 2 filiales directes. Exclus : distributeurs franchisés, fournisseurs tier-2. Périmètre financier = périmètre RSE.",
  gri_materialityProcess: "Analyse de double matérialité conduite en 2023 : entretiens avec 42 parties prenantes internes et externes, évaluation des impacts financiers et d'impact, validation par le Comité RSE et le Conseil d'Administration.",
  gri_materialTopics: ["Eau et effluents", "Émissions GES", "Santé et sécurité au travail", "Conditions de travail", "Approvisionnement responsable"],
  gri_sectorStandards: "GRI 13 – Agriculture, Aquaculture and Fishing Sectors : applicable pour les activités de production primaire. Non applicable pour les filiales de distribution.",
  gri_dataOwners: "Environnement/GES : Directeur HSE. Eau : Responsable technique. Social/RH : DRH. Gouvernance : DAF. Validation finale : Directeur RSE avec revue par le Comité d'Audit.",
};
